/**
 * Verify an enrollment certificate using the bundled Service Root public key.
 *
 * Certificate wire format (per `plan/security.md` §5.3):
 *
 *   <base64url(canonical_json(cert_object))> "." <base64url(sig_bytes)>
 *
 * where
 *
 *   sig_bytes = Ed25519_sign(SR_sk, BLAKE2b-256("dovach-enroll-v1\n" || cert_bytes))
 *
 * The domain-separation prefix is mandatory and must match exactly here
 * — without it, an attacker with `SR_sk` could cross-protocol replay
 * signatures from any other Dovach signing path.
 */

import { blake2b } from "@noble/hashes/blake2b";
import nacl from "tweetnacl";
import { decodeBase64, encodeUTF8 } from "tweetnacl-util";

import { loadSRPublicKey } from "./sr-pk";

const DOMAIN_SEPARATION = "dovach-enroll-v1\n";

export class CertVerifyError extends Error {
  override name = "CertVerifyError";
  constructor(
    message: string,
    readonly reason:
      | "malformed"
      | "bad_base64"
      | "bad_signature"
      | "bad_json"
      | "expired"
      | "wrong_account",
  ) {
    super(message);
  }
}

export interface EnrollmentCert {
  v: 2;
  account_id: string;
  office_pk: string;
  issued_at: string;
  not_after: string;
}

function decodeBase64Url(s: string): Uint8Array {
  // Strict URL-safe alphabet — match Python's urlsafe_b64decode and Go's
  // base64.URLEncoding behavior. Standard-base64 (`+`, `/`) blobs that
  // happen to leak in from a relay must NOT verify here when py/go would
  // reject them. The check is a single regex pass before any padding
  // manipulation.
  if (!/^[A-Za-z0-9_-]*$/.test(s)) {
    throw new Error("non-url-safe character in base64url input");
  }
  const padded = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(s.length + ((4 - (s.length % 4)) % 4), "=");
  return decodeBase64(padded);
}

function blake2b256(bytes: Uint8Array): Uint8Array {
  return blake2b(bytes, { dkLen: 32 });
}

/**
 * Compute the cert-fingerprint that the dashboard puts in the invite-URL
 * fragment. This must match what the API computes on the server side
 * (BLAKE2b-160 of the cert blob bytes, hex-encoded).
 */
export function fingerprintCertBlob(certBlob: string): string {
  const bytes = new TextEncoder().encode(certBlob);
  const out = blake2b(bytes, { dkLen: 20 });
  return Array.from(out)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Verify a cert blob and return the parsed certificate. Throws CertVerifyError
 * with a typed `reason` on any failure.
 *
 * If `expectedAccountId` is supplied, the cert's `account_id` MUST match
 * exactly (case-insensitive). Without this check an attacker who substitutes
 * a valid cert from a *different* account in the API response would pass
 * signature verification — the security model relies on the binding between
 * (URL, account, cert.account_id) being end-to-end checked.
 */
export async function verifyEnrollmentCert(
  certBlob: string,
  options?: { srPk?: Uint8Array; now?: Date; expectedAccountId?: string },
): Promise<EnrollmentCert> {
  const srPk = options?.srPk ?? loadSRPublicKey();
  const now = options?.now ?? new Date();

  const dot = certBlob.indexOf(".");
  if (dot < 0) {
    throw new CertVerifyError("missing '.' separator", "malformed");
  }
  const certPart = certBlob.slice(0, dot);
  const sigPart = certBlob.slice(dot + 1);
  if (!certPart || !sigPart) {
    throw new CertVerifyError("empty cert or signature segment", "malformed");
  }

  let certBytes: Uint8Array;
  let sigBytes: Uint8Array;
  try {
    certBytes = decodeBase64Url(certPart);
    sigBytes = decodeBase64Url(sigPart);
  } catch (err) {
    throw new CertVerifyError(`base64url decode failed: ${(err as Error).message}`, "bad_base64");
  }
  if (sigBytes.length !== 64) {
    throw new CertVerifyError(`signature must be 64 bytes; got ${sigBytes.length}`, "bad_signature");
  }

  // Hash with the mandatory domain-separation prefix.
  const prefixBytes = new TextEncoder().encode(DOMAIN_SEPARATION);
  const toHash = new Uint8Array(prefixBytes.length + certBytes.length);
  toHash.set(prefixBytes, 0);
  toHash.set(certBytes, prefixBytes.length);
  const digest = blake2b256(toHash);

  if (!nacl.sign.detached.verify(digest, sigBytes, srPk)) {
    throw new CertVerifyError("Ed25519 signature does not verify against SR_pk", "bad_signature");
  }

  let cert: EnrollmentCert;
  try {
    cert = JSON.parse(encodeUTF8(certBytes)) as EnrollmentCert;
  } catch (err) {
    throw new CertVerifyError(`cert is not valid JSON: ${(err as Error).message}`, "bad_json");
  }
  if (cert.v !== 2) {
    throw new CertVerifyError(`unknown cert version: ${cert.v}`, "bad_json");
  }
  // `new Date("garbage")` returns Invalid Date whose <=/>= comparisons all
  // return false. Without the explicit NaN guard, malformed `not_after`
  // would silently pass the expiry check (py/go reject as bad_json).
  const notAfter = new Date(cert.not_after);
  if (Number.isNaN(notAfter.getTime())) {
    throw new CertVerifyError(`unparseable not_after: ${cert.not_after}`, "bad_json");
  }
  if (notAfter <= now) {
    throw new CertVerifyError(`cert expired at ${cert.not_after}`, "expired");
  }
  // Defense-in-depth: reject certs claimed to be issued in the future.
  // None of the verifiers checked this previously — a clock-skew attack
  // (or a buggy signer) producing `issued_at = "2099-01-01"` would have
  // passed all three.
  const issuedAt = new Date(cert.issued_at);
  if (Number.isNaN(issuedAt.getTime())) {
    throw new CertVerifyError(`unparseable issued_at: ${cert.issued_at}`, "bad_json");
  }
  if (issuedAt > now) {
    throw new CertVerifyError(`cert not yet valid (issued_at=${cert.issued_at})`, "bad_json");
  }

  // Account-binding check — must match what the URL claimed.
  if (options?.expectedAccountId !== undefined) {
    const a = options.expectedAccountId.toLowerCase().replace(/-/g, "");
    const b = cert.account_id.toLowerCase().replace(/-/g, "");
    if (a !== b) {
      throw new CertVerifyError(
        `cert account_id ${cert.account_id} ≠ expected ${options.expectedAccountId}`,
        "wrong_account",
      );
    }
  }

  return cert;
}
